import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IJob } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'jobs'

const getAll = (axios: IFactoryFunctionRequest<Array<IJob>, undefined>) => ({ firmId }: { firmId: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/${firmId}`,
  })
}

const getAllFilter = (axios: IFactoryFunctionRequest<Array<Pick<IJob, '_id' | 'display'>>, undefined>) => (options = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firm/filter`,
    query: options,
  })
}

const listFromIds = (axios: IFactoryFunctionRequest<Array<Pick<IJob, '_id' | 'display'>>, undefined>) => (data) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/listFromIds`,
    data,
  })
}

/**
 * @deprecated use filter instead
 */
const getFilter = (axios: IFactoryFunctionRequest<Array<Pick<IJob, '_id' | 'display'>>, undefined>) => (options = {}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filterDeprecated`,
    query: options,
  })
}

const filter = (axios: IFactoryFunctionRequest<Array<Pick<IJob, '_id' | 'display'>>, undefined>) => (options = {}) => {
  const userRoute = `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`
  const candidateRoute = `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter/candidate`
  return axios({
    method: EMethod.get,
    url: options.firmId ? candidateRoute : userRoute,
    query: options,
  })
}

const getOneGantt = (axios: IFactoryFunctionRequest<IJob, undefined>) => (jobId) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/getGanttToAdd/${jobId}`,
  })
}

const getRecruitmentNewJob = (axios: IFactoryFunctionRequest<IJob, undefined>) => (jobId) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/recruitment/newPosition/${jobId}`,
  })
}

const getJobSkillMatrix = (axios: IFactoryFunctionRequest<IJob, undefined>) => ({ jobId }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/skillMatrix/${jobId}`,
  })
}

const superAdminList = (axios: IFactoryFunctionRequest<{ docs: Array<IJob>, count: { total: number, totalPages: number, currentPage: number, hasNextPage: boolean } }, undefined>) => ({ page = 0, limit = 30, search = '' }: { page?: number; limit?: number; search?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdminList`,
    query: { page, limit, search },
  })
}

const superAdminCreate = (axios: IFactoryFunctionRequest<IJob, Partial<IJob>>) => (data: Partial<IJob>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdmin`,
    data,
  })
}

const managementCreate = (axios: IFactoryFunctionRequest<IJob, Partial<IJob>>) => (data: Partial<IJob>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/management/create`,
    data,
  })
}

const superAdminUpdate = (axios: IFactoryFunctionRequest<IJob, Partial<IJob>>) => (jobId: string, data: Partial<IJob>) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/superAdmin/${jobId}`,
    data,
  })
}

const bulkCreateTemplate = (axios: IFactoryFunctionRequest<any, any>) => (firmId?: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/template`,
    query: firmId ? { firmId } : undefined,
  })
}

const bulkCreateValidate =
  (axios: IFactoryFunctionRequest<any, any>) => (file: File, firmId?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/validate`,
      query: firmId ? { firmId } : undefined,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

const bulkCreate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (rows: { data: Record<string, any>; resolvedData?: Record<string, any> }[], firmId?: string) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create`,
      data: {
        rows,
        ...(firmId ? { firmId } : {}),
      },
    })
  }

export default (axios: AxiosInstance) => ({
  getAll: getAll(useAxios(axios)),
  getAllFilter: getAllFilter(useAxios(axios)),
  /**
   * @deprecated use filter instead
   */
  getFilter: getFilter(useAxios(axios)),
  getOneGantt: getOneGantt(useAxios(axios)),
  getRecruitmentNewJob: getRecruitmentNewJob(useAxios(axios)),
  filter: filter(useAxios(axios)),
  getJobSkillMatrix: getJobSkillMatrix(useAxios(axios)),
  listFromIds: listFromIds(useAxios(axios)),
  superAdminList: superAdminList(useAxios(axios)),
  superAdminCreate: superAdminCreate(useAxios(axios)),
  managementCreate: managementCreate(useAxios(axios)),
  superAdminUpdate: superAdminUpdate(useAxios(axios)),
  bulkCreateTemplate: bulkCreateTemplate(useAxios(axios)),
  bulkCreateValidate: bulkCreateValidate(useAxios(axios)),
  bulkCreate: bulkCreate(useAxios(axios)),
})
